import shouldForwardProp from '@styled-system/should-forward-prop';
import styled, { css } from 'styled-components';

import palette from '../../../theme/palette';

const variants = {
  subtle: {
    grey: {
      normal: { color: palette.greyscale[10] },
      inverted: { color: palette.inverted.white },
    },
    green: {
      normal: { color: palette.support.success[10] },
      inverted: { color: palette.inverted.white },
    },
    red: {
      normal: { color: palette.support.alert[10] },
      inverted: { color: palette.inverted.white },
    },
    blue: {
      normal: { color: palette.support.info[10] },
      inverted: { color: palette.inverted.white },
    },
    orange: {
      normal: { color: palette.support.warning[10] },
      inverted: { color: palette.inverted.white },
    },
    purple: {
      normal: { color: palette.additional.purple[10] },
      inverted: { color: palette.inverted.white },
    },
    magenta: {
      normal: { color: palette.additional.magenta[10] },
      inverted: { color: palette.inverted.white },
    },
    yellow: {
      normal: { color: palette.additional.yellow[10] },
      inverted: { color: palette.inverted.white },
    },
    teal: {
      normal: { color: palette.additional.teal[10] },
      inverted: { color: palette.inverted.white },
    },
  },
  strong: {
    grey: {
      normal: { color: palette.greyscale[60] },
      inverted: { color: palette.inverted.white },
    },
    green: {
      normal: { color: palette.support.success[60] },
      inverted: { color: palette.inverted.white },
    },
    red: {
      normal: { color: palette.support.alert[60] },
      inverted: { color: palette.inverted.white },
    },
    blue: {
      normal: { color: palette.support.info[60] },
      inverted: { color: palette.inverted.white },
    },
    orange: {
      normal: { color: palette.support.warning[60] },
      inverted: { color: palette.inverted.white },
    },
    purple: {
      normal: { color: palette.additional.purple[70] },
      inverted: { color: palette.inverted.white },
    },
    magenta: {
      normal: { color: palette.additional.magenta[60] },
      inverted: { color: palette.inverted.white },
    },
    yellow: {
      normal: { color: palette.additional.yellow[30] },
      inverted: { color: palette.inverted.white },
    },
    teal: {
      normal: { color: palette.additional.teal[60] },
      inverted: { color: palette.inverted.white },
    },
  },
} as const;

export type Variant = keyof typeof variants;
export type Color = keyof typeof variants[Variant];

type Props = Readonly<{
  inverted?: boolean;
  hasIcon?: boolean;
  variant?: Variant;
  fullWidth?: boolean;
  color: Color;
}>;

const badgePaddings = ({ hasIcon = false }: Props) => {
  if (hasIcon) {
    return css`
      padding: ${({ theme }) => theme.scTheme.space[200]};
      padding-right: ${({ theme }) => theme.scTheme.space[300]};
    `;
  }

  return css`
    padding: ${({ theme }) => theme.scTheme.space[200]} ${({ theme }) => theme.scTheme.space[300]};
  `;
};

const BadgeBase = styled.div.withConfig({ shouldForwardProp })<Props>`
  align-items: center;
  background-color: ${({ variant = 'subtle', color, inverted = false }) => variants[variant][color][inverted ? 'inverted' : 'normal'].color};
  border-radius: ${({ theme }) => theme.scTheme.borders.radii.md};
  display: flex;
  height: max-content;
  ${badgePaddings};
  text-transform: uppercase;
  width: ${({ fullWidth = false }) => (fullWidth ? '100%' : 'max-content')};
`;

export default BadgeBase;
